import {
  ErrorCode,
  MenuTreeHierarchyItem,
  MenuTreeHierarchyProduct,
  MenuTreeHierarchyProductStockCount,
  OrderFormat,
  PurchaseProductDetails,
  SystemVisibility,
} from '@ancon/wildcat-types'
import isDetailedProductOutOfStock from '@ancon/wildcat-utils/inventory/isDetailedProductOutOfStock'
import createCodedError from '@ancon/wildcat-utils/error/createCodedError'
import { orderBy } from 'lodash'
import getDetailedProductVariantStock from '@ancon/wildcat-utils/inventory/getDetailedProductVariantStock'
import getFormattedUTCDateTime from '@ancon/wildcat-utils/date/getFormattedUTCDateTime'

import api from '../../../api'
import createAppAsyncThunk from '../../../store/createAppAsyncThunk'
import {
  outletFiltersSelector,
  outletListItemSelector,
  outletSelectedOutletIdSelector,
} from '../../outlet/store/outletSelector'
import { appShowError } from '../../app/store/appSlice'
import getTranslatedError from '../../app/utils/getTranslatedError'
import { ErrorModalType } from '../../app/types'
import {
  checkoutCurrentCheckoutOutletIdSelector,
  checkoutCurrentCheckoutOutletListItemSelector,
  checkoutCurrentCheckoutSelectedItemIdSelector,
  checkoutFiltersSelector,
} from '../../checkout/store/checkoutSelectors'
import { appLanguageSelector } from '../../app/store/appSelectors'
import { ProductsSearchAPILimit } from '../constants'
import { outletSelectedTenantGuestGroupIdSelector } from '../../outlets/store/outletsSelectors'

import { productByIdSelector } from './productSelectors'

export const fetchProductDetails = createAppAsyncThunk<
  {
    productDetails: PurchaseProductDetails
    productMenuStocks?: MenuTreeHierarchyProductStockCount[]
  },
  {
    productId: string
    outletId?: string | null
    orderFormat?: OrderFormat | null
    orderDateTime?: string
  }
>(
  'product/fetchProductDetails',
  async (
    { productId, outletId, orderFormat, orderDateTime },
    { getState, dispatch },
  ) => {
    const checkoutOutletId = checkoutCurrentCheckoutOutletIdSelector(getState())
    const selectedCheckoutItemId =
      checkoutCurrentCheckoutSelectedItemIdSelector(getState())
    const selectedOutletId = outletSelectedOutletIdSelector(getState())
    const outletFilters = outletFiltersSelector(getState())
    const currentOrderFormat = orderFormat ?? outletFilters.orderFormat!
    const currentOrderTime =
      (orderDateTime ?? outletFilters.serviceTime?.time) || undefined
    const currentOutletId =
      outletId ?? (selectedCheckoutItemId ? checkoutOutletId : selectedOutletId)
    const selectedOutletTenantGuestGroupId =
      outletSelectedTenantGuestGroupIdSelector(getState())

    const response = await api.core.products.get.details({
      productId,
      outletId: currentOutletId!,
      systemVisibility: SystemVisibility.OrderWeb,
      orderFormat: currentOrderFormat,
      purchaseOrderFormat: currentOrderFormat,
      orderTime: getFormattedUTCDateTime(currentOrderTime),
      ...(selectedOutletTenantGuestGroupId && {
        guestGroupId: selectedOutletTenantGuestGroupId,
      }),
    })

    const { purchaseProduct } = response.data!

    const {
      variants,
      ingredients,
      addOnGroups,
      hasAddOnGroups,
      hasIngredients,
      hasVariants,
      upsellGroups,
    } = purchaseProduct

    const organizedProductDetails = {
      ...purchaseProduct,
      ...(hasIngredients && {
        ingredients: orderBy(ingredients, 'position', 'asc'),
      }),
      ...(hasVariants && {
        variants: orderBy(variants, 'position', 'asc'),
      }),
      ...(upsellGroups.length && {
        upsellGroups: orderBy(upsellGroups, 'position', 'asc').map(
          upsellGroup => ({
            ...upsellGroup,
            products: orderBy(upsellGroup.products, 'position', 'asc'),
          }),
        ),
      }),
      ...(hasAddOnGroups && {
        addOnGroups: orderBy(addOnGroups, 'position', 'asc').map(
          addonGroup => ({
            ...addonGroup,
            products: orderBy(addonGroup.products, 'position', 'asc').map(
              product => ({
                ...product,
                variants: orderBy(product.variants, 'position', 'asc'),
              }),
            ),
            ingredients: orderBy(addonGroup.ingredients, 'position', 'asc'),
          }),
        ),
      }),
    }

    const isProductOutOfStock = isDetailedProductOutOfStock(
      currentOutletId!,
      variants,
    )

    if (isProductOutOfStock) {
      const locale = appLanguageSelector(getState())
      const error = await getTranslatedError(
        createCodedError('Out of Stock', ErrorCode.ProductOutOfStock),
        undefined,
        locale,
      )

      dispatch(
        appShowError({
          ...error,
          errorModalType: ErrorModalType.ProductOutOfStock,
        }),
      )

      const productEntity = productByIdSelector(getState(), productId)
      if (productEntity) {
        const productMenuStocks = variants.map(variant => {
          const variantStock = getDetailedProductVariantStock(
            variant,
            currentOutletId!,
          )!
          return {
            stockProductId: variantStock.stockProductId,
            count: variantStock.stockCount,
            reorderCount: variantStock.reorderCount,
            variantId: variant.id,
          }
        })

        return {
          productDetails: organizedProductDetails,
          productMenuStocks,
        }
      }
    }

    return { productDetails: organizedProductDetails }
  },
)

export const fetchOutletMenuGroups = createAppAsyncThunk<
  MenuTreeHierarchyItem[],
  { overrideMenuTreeId?: string | null }
>(
  'product/fetchOutletMenuGroups',
  async ({ overrideMenuTreeId = null }, { getState }) => {
    const outlet = outletListItemSelector(getState())
    const outletFilters = outletFiltersSelector(getState())

    if (outlet) {
      const response = await api.core.menuTrees.get.hierarchy({
        outletId: outlet.id,
        menuTreeId: overrideMenuTreeId || outlet.mandatoryMenuTreeId,
        orderFormats:
          outlet.showAvailableProductsFromOtherOrderFormats ||
          !outletFilters?.orderFormat
            ? OrderFormat.None
            : outletFilters.orderFormat,
        purchaseOrderFormat: outletFilters?.orderFormat || undefined,
        systemVisibility: SystemVisibility.OrderWeb,
        orderTime: getFormattedUTCDateTime(
          outletFilters.serviceTime?.time || undefined,
        ),
      })

      const menuGroups = (response?.data?.menuTreeItems || []).sort(
        (a, b) => a.sortOrder - b.sortOrder,
      )

      return menuGroups
    }

    return []
  },
)

export const searchMenuTreeProducts = createAppAsyncThunk<
  MenuTreeHierarchyProduct[],
  { search: string; limit?: number; offset?: number }
>(
  'product/searchMenuTreeProducts',
  async (
    { search, limit = ProductsSearchAPILimit, offset = 0 },
    { getState },
  ) => {
    const outlet = checkoutCurrentCheckoutOutletListItemSelector(getState())
    const outletFilters = checkoutFiltersSelector(getState())

    if (outlet) {
      const response = await api.core.menuTrees.get.products({
        search,
        limit,
        offset,
        outletId: outlet.id,
        menuTreeId: outlet.mandatoryMenuTreeId,
        orderFormats:
          outlet.showAvailableProductsFromOtherOrderFormats ||
          !outletFilters?.orderFormat
            ? OrderFormat.None
            : outletFilters.orderFormat,
        purchaseOrderFormat: outletFilters?.orderFormat || undefined,
        systemVisibility: SystemVisibility.OrderWeb,
        orderTime: getFormattedUTCDateTime(
          outletFilters.serviceTime?.time || undefined,
        ),
      })

      return response.data.items
    }

    return []
  },
)
